import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

function formatDate(pubDate) {
  const d = new Date(pubDate);
  if (Number.isNaN(d.getTime())) return pubDate;

  const pad = (n) => String(n).padStart(2, "0");
  const day = pad(d.getDate());
  const month = pad(d.getMonth() + 1);
  const year = d.getFullYear();

  let hours = d.getHours();
  const minutes = pad(d.getMinutes());
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;

  return `${day}/${month}/${year}, ${pad(hours)}:${minutes} ${ampm}`;
}

export default function ExploreNews() {
  const { t } = useTranslation();
  const [feedItems, setFeedItems] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const url = "https://www.investing.com/rss/news.rss";
        const response = await axios.get(
          "https://corsproxy.io/?url=" + encodeURIComponent(url)
        );

        const parser = new DOMParser();
        const xml = parser.parseFromString(response.data, "application/xml");
        const items = xml.querySelectorAll("item");

        const parsed = Array.from(items).map((item) => {
          const enclosure = item.querySelector("enclosure");
          const media = item.querySelector("media\\:content");
          const img =
            enclosure?.getAttribute("url") || media?.getAttribute("url") || "";

          return {
            title: item.querySelector("title")?.textContent?.trim() || "",
            link: item.querySelector("link")?.textContent?.trim() || "",
            pubDate: item.querySelector("pubDate")?.textContent?.trim() || "",
            author:
              item.querySelector("author")?.textContent?.trim() ||
              "investing.com",
            imageUrl: img,
          };
        });

        setFeedItems(parsed);
      } catch (e) {
        console.error("Error fetching rss:", e);
      }
    };

    fetchData();
  }, []);

  const slides = useMemo(() => feedItems.slice(0, 12), [feedItems]);

  return (
    <section className="relative overflow-hidden mt-[80px] xl:mt-[140px]">
      <div className="max-w-[1220px] mx-[20px] xl:mx-auto">
        {/* Header row */}
        <div className="flex items-center justify-between gap-6">
          <h2 className="text-[#061A77] font-[600] leading-[1.2] poppins text-[26px] xl:text-[40px]">
            {t("home.37")}
          </h2>

          {/* Arrows with CLASS SELECTORS */}
          <div className="flex items-center gap-3">
            <button
              className="news-prev w-[46px] h-[22px] xl:w-[56px] xl:h-[22px] rounded-[2px] border border-[#061A77] text-[#061A77] flex items-center justify-center hover:bg-[#061A77] hover:text-white transition"
              type="button"
              aria-label="Previous"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M15 18l-6-6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            <button
              className="news-next w-[46px] h-[22px] xl:w-[56px] xl:h-[22px] rounded-[2px] bg-[#061A77] text-white flex items-center justify-center hover:opacity-90 transition"
              type="button"
              aria-label="Next"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 6l6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Slider */}
        <div className="mt-8">
          <Swiper
            modules={[Navigation, A11y]}
            spaceBetween={24}
            slidesPerView={1}
            navigation={{
              prevEl: ".news-prev",
              nextEl: ".news-next",
            }}
            breakpoints={{
              640: { slidesPerView: 1.2 },
              768: { slidesPerView: 2 },
              1280: { slidesPerView: 3 },
            }}
          >
            {slides.map((item, idx) => (
              <SwiperSlide key={`${item.link}-${idx}`}>
                <article className="rounded-[10px] overflow-hidden bg-white">
                  <div className="h-[150px] xl:h-[150px] relative overflow-hidden">
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200" />
                    )}

                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="px-6 py-2 rounded-full bg-white/75 backdrop-blur-sm">
                        <p className="text-[14px] text-[#6B7280] font-[500]">
                          {item.author || "investing.com"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="py-4 xl:py-5">
                    <p className="text-[#111827] font-[500] leading-snug text-[15px] xl:text-[16px] line-clamp-2 min-h-[44px]">
                      {item.title}
                    </p>

                    <a
                      href={item.link}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block mt-3 text-[#25B7C4] text-[14px] underline"
                    >
                      {t("news.3") || "Read More"}
                    </a>

                    <p className="mt-4 text-[#111827] text-[13px]">
                      {formatDate(item.pubDate)}
                    </p>
                  </div>
                </article>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
}
